import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ITooltipParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { Box, Paper, Typography } from '@mui/material';

import { SimulationSummary } from '@octopus/termination-api';
import {
  DataGridSkeleton,
  DataGridToolbar,
  useDataGrid,
} from '@octopus/ui/data-grid';
import { ContentTabs, agGridThemes } from '@octopus/ui/shared-components';

import { TABS } from '../../constants';
import { useFilters } from '../../hooks/useFilters';
import { useSearchEmployees } from '../../hooks/useSearchEmployees';
import { useSearchSimulations } from '../../hooks/useSearchSimulations';
import { useTerminationSimulationActions } from '../../hooks/useTerminationSimulationActions';
import { useTerminationSimulationDrawer } from '../../hooks/useTerminationSimulationDrawer';
import { ContractSummaryWithFgtsBalance } from '../../types';
import { EmployeeDrawer } from '../drawer/EmployeeDrawer';
import { TerminationSimulationForm } from '../form/Form';
import { PageActionBar } from '../PageActionBar';

import {
  defaultColDef,
  getColumns,
  handleSortChanged,
  rowSelection,
  selectionColumnDef,
} from './Columns';
export type TerminationSimulationTableProps = { organizationId: string };

export function TerminationSimulationTable({
  organizationId,
}: TerminationSimulationTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get('tab') || 'collaborators',
  );

  const filters = useFilters();
  const dataGridProps = useDataGrid({
    filters,
    key: `termination-simulation-${organizationId}`,
    sorting: { field: 'name', order: 'asc' },
    search: { type: 'state' },
  });

  const getContracts = useSearchEmployees({
    organizationId,
    activeTab,
    dataGridProps,
  });

  const getSimulations = useSearchSimulations({
    organizationId,
    activeTab,
    dataGridProps,
  });

  const { data, isLoading } =
    activeTab === 'collaborators' ? getContracts : getSimulations;

  const drawer = useTerminationSimulationDrawer(data?.data);
  const actions = useTerminationSimulationActions();
  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
    setActiveTab(tab);
  };

  return (
    <>
      <DataGridToolbar
        filters={filters}
        searchProps={dataGridProps.searchProps}
        filteringProps={dataGridProps.filteringProps}
        searchPlaceholder="Procurar"
      />

      <ContentTabs
        selected={activeTab}
        setSelected={handleTabChange}
        tabs={TABS}
      />

      <Box height={400} width="100%">
        {isLoading ? (
          <DataGridSkeleton columnsCount={5} rowsCount={10} />
        ) : (
          <AgGridReact<ContractSummaryWithFgtsBalance | SimulationSummary>
            ref={actions.tableRef}
            theme={agGridThemes.auditing}
            onSortChanged={handleSortChanged(dataGridProps)}
            loading={isLoading}
            rowData={data?.data as ContractSummaryWithFgtsBalance[]}
            domLayout="autoHeight"
            defaultColDef={defaultColDef}
            rowSelection={rowSelection}
            selectionColumnDef={selectionColumnDef}
            onCellClicked={drawer.handleOpenDrawer}
            onSelectionChanged={actions.handleSelectionChanged}
            components={{ TruncatedTooltip }} // ← registra aqui
            tooltipShowMode="whenTruncated"
            tooltipMouseTrack
            tooltipInteraction
            tooltipShowDelay={0}
            suppressMultiSort
            suppressCellFocus
            suppressMovableColumns
            columnDefs={getColumns({
              activeTab,
              onSelectContract: (params) => {
                actions.setSelectedContracts([params.data]);
                actions.handleOpenForm();
              },
            })}
          />
        )}
      </Box>

      <PageActionBar
        selectedCount={actions.selectedContractsCount}
        onCancel={actions.handleUnselectAll}
        onSimulate={actions.handleOpenForm}
      />

      <TerminationSimulationForm
        open={actions.formOpen}
        selectedContracts={
          (actions.selectedContracts || [
            drawer.selectedItem,
          ]) as ContractSummaryWithFgtsBalance[]
        }
        onClose={actions.handleCloseForm}
      />

      <EmployeeDrawer
        open={drawer.drawerOpen}
        onClose={drawer.handleCloseDrawer}
        selectedContract={drawer.selectedItem as ContractSummaryWithFgtsBalance}
        controls={drawer.controls}
      />
    </>
  );
}

const TruncatedTooltip = (props: ITooltipParams) => {
  const text = props.valueFormatted ?? props.value;
  return (
    <Paper
      elevation={4}
      sx={{
        padding: 1,
        maxWidth: 420,
        overflowWrap: 'break-word',
        backgroundColor: 'secondary.dark',
        color: 'text.primary',
      }}
    >
      <Typography variant="body2" color="secondary.main">
        {text}
      </Typography>
    </Paper>
  );
};
